import { getAdminClient } from '@beatq/sdk-client';
import { caseAnalytics, type AnalyticsCase } from './dashboardAnalyticsModel.js';
import {
  mapErrorCode,
  readDataFromEnvelope,
  readListFromEnvelope,
  setAccessToken,
} from '@beatq/shared';

type ApiErr = { error?: { code: string; message: string } };

export type CasePriorityBucket = { priority: string; count: number };

export type OperatorAlert = {
  id: string;
  title: string;
  severity?: string | null;
  read?: boolean | null;
};

export type AdminDashboardSummary = {
  caseCount: number;
  /** Priority buckets when case payloads expose `priority`; otherwise empty. */
  casePriorityBuckets: CasePriorityBucket[];
  /** Open/assigned to current agent when list filter `assigneeId=me` is supported. */
  myAssignedCaseCount: number;
  /** 07-08: cases at risk of SLA breach (slaDueAt within 30m or breachRisk). */
  breachRiskCount: number;
  moderationCount: number;
  fraudCount: number;
  emergencyActive: boolean;
  notificationsHealthy: boolean;
  unreadAlertCount: number;
  alerts: OperatorAlert[];
  failedRegions: string[];
  activity?: { cases: number[]; assigned: number[]; moderation: number[]; breach: number[] };
};

const BREACH_HORIZON_MS = 30 * 60 * 1000;

export type AdminPermission = {
  code?: string;
  granted?: boolean;
};

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function readAccessTokenFromLoginEnvelope(envelope: unknown): string | null {
  if (envelope == null || typeof envelope !== 'object') return null;
  const rec = envelope as {
    data?: { accessToken?: string; tokens?: { accessToken?: string } };
    tokens?: { accessToken?: string };
    accessToken?: string;
  };
  return (
    rec.data?.tokens?.accessToken ??
    rec.data?.accessToken ??
    rec.tokens?.accessToken ??
    rec.accessToken ??
    null
  );
}

export function isCaseBreachRisk(row: unknown, now = Date.now()): boolean {
  if (row == null || typeof row !== 'object') return false;
  const item = row as { breachRisk?: boolean | null; slaDueAt?: string | Date | null };
  if (item.breachRisk === true) return true;
  if (item.breachRisk === false) return false;
  if (item.slaDueAt == null) return false;
  const due = Date.parse(
    typeof item.slaDueAt === 'string' ? item.slaDueAt : item.slaDueAt.toISOString(),
  );
  if (Number.isNaN(due)) return false;
  return due <= now + BREACH_HORIZON_MS;
}

export function breachRiskCountFromCases(cases: unknown[], now = Date.now()): number {
  return cases.filter((row) => isCaseBreachRisk(row, now)).length;
}

export function itemsFromEnvelope<T = unknown>(envelope: unknown): T[] {
  return readListFromEnvelope<T>(envelope, ['items', 'cases', 'queue', 'data']);
}

export function permissionsFromEnvelope(envelope: unknown): AdminPermission[] {
  return readListFromEnvelope<AdminPermission>(envelope, ['permissions']);
}

export function emergencyActiveFromEnvelope(envelope: unknown): boolean {
  if (envelope == null || typeof envelope !== 'object') return false;
  const payload =
    readDataFromEnvelope<{
      active?: boolean;
      status?: string;
      controls?: Array<{ enabled?: boolean; status?: string }>;
    }>(envelope) ??
    (envelope as {
      active?: boolean;
      status?: string;
      controls?: Array<{ enabled?: boolean; status?: string }>;
    });
  if (Array.isArray(payload.controls) && payload.controls.length > 0) {
    return payload.controls.some((c) => c.status === 'PAUSED' || c.enabled === false);
  }
  return payload.active === true || payload.status === 'active';
}

export function notificationsHealthyFromEnvelope(envelope: unknown): boolean {
  if (envelope == null || typeof envelope !== 'object') return true;
  const payload =
    readDataFromEnvelope<{
      healthy?: boolean;
      status?: string;
      channels?: Array<{ healthy?: boolean }>;
    }>(envelope) ??
    (envelope as {
      healthy?: boolean;
      status?: string;
      channels?: Array<{ healthy?: boolean }>;
    });
  if (Array.isArray(payload.channels) && payload.channels.length > 0) {
    return payload.channels.every((c) => c.healthy !== false);
  }
  if (typeof payload.healthy === 'boolean') {
    return payload.healthy;
  }
  return payload.status !== 'degraded' && payload.status !== 'down';
}

export function priorityBucketsFromCases(cases: unknown[]): CasePriorityBucket[] {
  const counts = new Map<string, number>();
  for (const row of cases) {
    if (row == null || typeof row !== 'object') continue;
    const priority = (row as { priority?: string }).priority;
    if (!priority) continue;
    counts.set(priority, (counts.get(priority) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([priority, count]) => ({ priority, count }))
    .sort((a, b) => b.count - a.count);
}

export function alertsFromEnvelope(envelope: unknown): OperatorAlert[] {
  return itemsFromEnvelope(envelope).flatMap((row) => {
    if (row == null || typeof row !== 'object') return [];
    const item = row as {
      id?: string;
      dispatchId?: string;
      title?: string;
      severity?: string | null;
      read?: boolean | null;
    };
    const id = item.dispatchId ?? item.id;
    if (!id) return [];
    return [
      {
        id,
        title: item.title ?? id,
        severity: item.severity,
        read: item.read,
      },
    ];
  });
}

export function dashboardSummaryFromResponses(
  responses: {
    cases: unknown;
    myCases: unknown;
    moderation: unknown;
    fraud: unknown;
    emergency: unknown;
    notifications: unknown;
    inbox: unknown;
  },
  failedRegions: string[] = [],
  now = Date.now(),
): AdminDashboardSummary {
  const caseItems = itemsFromEnvelope(responses.cases);
  const alerts = alertsFromEnvelope(responses.inbox);
  return {
    caseCount: caseItems.length,
    casePriorityBuckets: priorityBucketsFromCases(caseItems),
    myAssignedCaseCount: itemsFromEnvelope(responses.myCases).length,
    breachRiskCount: breachRiskCountFromCases(caseItems, now),
    moderationCount: itemsFromEnvelope(responses.moderation).length,
    fraudCount: itemsFromEnvelope(responses.fraud).length,
    emergencyActive: emergencyActiveFromEnvelope(responses.emergency),
    notificationsHealthy: notificationsHealthyFromEnvelope(responses.notifications),
    unreadAlertCount: alerts.filter((a) => a.read !== true).length,
    alerts,
    failedRegions,
    activity: {
      breach: caseAnalytics(
        caseItems.filter((row) => isCaseBreachRisk(row, now)) as AnalyticsCase[],
        7,
        new Date(now),
      ).trend.map((day) => day.created),
      cases: caseAnalytics(caseItems as AnalyticsCase[], 7, new Date(now)).trend.map(
        (day) => day.created,
      ),
      assigned: caseAnalytics(
        itemsFromEnvelope(responses.myCases) as AnalyticsCase[],
        7,
        new Date(now),
      ).trend.map((day) => day.created),
      moderation: caseAnalytics(
        itemsFromEnvelope(responses.moderation) as AnalyticsCase[],
        7,
        new Date(now),
      ).trend.map((day) => day.created),
    },
  };
}

export async function adminLogin(body: { email: string; password: string }) {
  const client = getAdminClient();
  const { data, error, response } = await client.POST('/admin/v1/auth/login', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function verifyAdminMfa(body: {
  code: string;
  challengeToken: string;
  factorId?: string;
}) {
  const client = getAdminClient();
  const { data, error, response } = await client.POST('/admin/v1/auth/mfa/verify', {
    body: {
      code: body.code,
      challengeToken: body.challengeToken,
      ...(body.factorId ? { factorId: body.factorId } : {}),
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  const accessToken = readAccessTokenFromLoginEnvelope(data);
  if (!accessToken) {
    throw new Error('Second-factor verification did not return an access token');
  }
  setAccessToken(accessToken);
  return data;
}

export async function listAdminMfaFactors() {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/auth/mfa/factors');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getAdminPermissions() {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/auth/me/permissions');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listCases(query?: {
  status?: string;
  state?: string;
  assigneeId?: string;
  limit?: number;
}) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/cases', {
    params: query ? { query: query as never } : undefined,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listModerationQueue(query?: { status?: string }) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/moderation/queue', {
    params: query ? { query: query as never } : undefined,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getEmergencyStatus() {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/emergency/status');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getNotificationHealth() {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/notifications/health');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listRiskQueue(query?: { state?: string; status?: string }) {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/risk/cases', {
    params: query ? { query: query as never } : undefined,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listOperatorInbox() {
  const client = getAdminClient();
  const { data, error, response } = await client.GET('/admin/v1/notifications/inbox');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

async function settleDashboardCall<T>(
  fn: () => Promise<T>,
  fallback: T,
): Promise<{ ok: boolean; value: T }> {
  try {
    return { ok: true, value: await fn() };
  } catch {
    return { ok: false, value: fallback };
  }
}

const DASHBOARD_REGION_LABELS = [
  'cases',
  'assigned cases',
  'moderation',
  'fraud',
  'incidents',
  'notification health',
  'alerts',
] as const;

export async function loadDashboardSummary(): Promise<AdminDashboardSummary> {
  const empty = { data: [] };
  const settled = await Promise.all([
    settleDashboardCall(() => listCases({ state: 'OPEN' }), empty),
    settleDashboardCall(() => listCases({ assigneeId: 'me' }), empty),
    settleDashboardCall(() => listModerationQueue(), empty),
    settleDashboardCall(() => listRiskQueue(), empty),
    settleDashboardCall(() => getEmergencyStatus(), { data: { controls: [] } }),
    settleDashboardCall(() => getNotificationHealth(), { data: { channels: [] } }),
    settleDashboardCall(() => listOperatorInbox(), empty),
  ]);
  const failedRegions = DASHBOARD_REGION_LABELS.filter((_, i) => !settled[i]?.ok);
  return dashboardSummaryFromResponses(
    {
      cases: settled[0]!.value,
      myCases: settled[1]!.value,
      moderation: settled[2]!.value,
      fraud: settled[3]!.value,
      emergency: settled[4]!.value,
      notifications: settled[5]!.value,
      inbox: settled[6]!.value,
    },
    failedRegions,
  );
}
